/**
 * 管理端API集合
 * 
 * ⚠️ 重要：所有API端点必须使用 API_ENDPOINTS 常量
 * 不要硬编码URL路径，避免路径错误导致404
 */
import { apiClient } from './client'
import { API_ENDPOINTS, buildUrl } from './endpoints'

export const adminApi = {
  // ========== 用户管理 ==========
  
  /**
   * 获取用户的AI调用记录
   * @param userId 用户ID
   * @param page 页码
   * @param pageSize 每页数量
   */
  getUserLLMLogs: async (userId: number, page: number = 1, pageSize: number = 20) => {
    const url = buildUrl(API_ENDPOINTS.USERS.LLM_LOGS(userId), {
      page,
      page_size: pageSize
    })
    return apiClient.get(url)
  },

  /**
   * 获取全局LLM调用记录（包含Token使用信息）
   * @param params 查询参数
   */
  getAllLLMLogs: async (params?: {
    page?: number
    page_size?: number
    user_id?: number
    start_date?: string
    end_date?: string
    provider?: string
    model?: string
    purpose?: string
  }) => {
    return apiClient.get(API_ENDPOINTS.USERS.ALL_LLM_LOGS, { params })
  },

  /**
   * 获取用户详情
   * @param userId 用户ID
   */
  getUserDetail: async (userId: number) => {
    return apiClient.get(API_ENDPOINTS.USERS.DETAIL(userId))
  },

  /**
   * 获取用户列表
   * @param params 查询参数
   */
  getUserList: async (params?: any) => {
    return apiClient.get(API_ENDPOINTS.USERS.LIST, { params })
  },

  /**
   * 创建用户
   * @param data 用户数据
   */
  createUser: async (data: any) => {
    return apiClient.post(API_ENDPOINTS.USERS.CREATE, data)
  },

  /**
   * 更新用户
   * @param userId 用户ID
   * @param data 用户数据
   */
  updateUser: async (userId: number, data: any) => {
    return apiClient.put(API_ENDPOINTS.USERS.UPDATE(userId), data)
  },

  /**
   * 删除用户
   * @param userId 用户ID
   */
  deleteUser: async (userId: number) => {
    return apiClient.delete(API_ENDPOINTS.USERS.DELETE(userId))
  },

  /**
   * 切换用户状态
   * @param userId 用户ID
   */
  toggleUserStatus: async (userId: number) => {
    return apiClient.post(API_ENDPOINTS.USERS.TOGGLE_STATUS(userId))
  },

  /**
   * 重置用户密码
   * @param userId 用户ID
   * @param newPassword 新密码
   */
  resetUserPassword: async (userId: number, newPassword: string) => {
    return apiClient.post(API_ENDPOINTS.USERS.RESET_PASSWORD(userId), {
      new_password: newPassword
    })
  },

  // ========== 统计分析 ==========
  
  /**
   * 获取统计概览
   */
  getOverview: async () => {
    return apiClient.get(API_ENDPOINTS.STATS.OVERVIEW)
  },

  /**
   * 获取每日统计
   * @param days 天数
   */
  getDailyStats: async (days: number = 7) => {
    return apiClient.get(API_ENDPOINTS.STATS.DAILY, {
      params: { days }
    })
  },

  /**
   * 获取用户增长数据
   * @param days 天数
   */
  getUserGrowth: async (days: number = 30) => {
    return apiClient.get(API_ENDPOINTS.STATS.USER_GROWTH, {
      params: { days }
    })
  },

  /**
   * 获取内容统计
   */
  getContentStats: async () => {
    return apiClient.get(API_ENDPOINTS.STATS.CONTENT_STATS)
  },

  /**
   * 获取Token使用统计
   * @param days 天数
   */
  getTokenUsage: async (days: number = 7) => {
    return apiClient.get(API_ENDPOINTS.STATS.TOKEN_USAGE, {
      params: { days }
    })
  },

  /**
   * 获取活跃用户统计
   * @param days 天数
   */
  getActiveUsers: async (days: number = 7) => {
    return apiClient.get(API_ENDPOINTS.STATS.ACTIVE_USERS, {
      params: { days }
    })
  },

  // ========== 内容管理 ==========
  
  /**
   * 获取内容列表
   * @param params 查询参数
   */
  getContentList: async (params?: any) => {
    return apiClient.get(API_ENDPOINTS.CONTENT.LIST, { params })
  },

  /**
   * 获取内容详情
   * @param contentId 内容ID
   */
  getContentDetail: async (contentId: number) => {
    return apiClient.get(API_ENDPOINTS.CONTENT.DETAIL(contentId))
  },

  /**
   * 创建内容
   * @param data 内容数据
   */
  createContent: async (data: any) => {
    return apiClient.post(API_ENDPOINTS.CONTENT.CREATE, data)
  },

  /**
   * 更新内容
   * @param contentId 内容ID
   * @param data 内容数据
   */
  updateContent: async (contentId: number, data: any) => {
    return apiClient.put(API_ENDPOINTS.CONTENT.UPDATE(contentId), data)
  },

  /**
   * 删除内容
   * @param contentId 内容ID
   */
  deleteContent: async (contentId: number) => {
    return apiClient.delete(API_ENDPOINTS.CONTENT.DELETE(contentId))
  },

  /**
   * 批量删除内容
   * @param contentIds 内容ID数组
   */
  batchDeleteContent: async (contentIds: number[]) => {
    return apiClient.post(API_ENDPOINTS.CONTENT.BATCH_DELETE, {
      content_ids: contentIds
    })
  },

  // ========== 系统管理 ==========
  
  /**
   * 获取系统设置
   */
  getSystemSettings: async () => {
    return apiClient.get(API_ENDPOINTS.SYSTEM.SETTINGS)
  },

  /**
   * 更新系统设置
   * @param settings 设置数据
   */
  updateSystemSettings: async (settings: any) => {
    return apiClient.put(API_ENDPOINTS.SYSTEM.UPDATE_SETTINGS, settings)
  },

  /**
   * 获取系统日志
   * @param params 查询参数
   */
  getSystemLogs: async (params?: any) => {
    return apiClient.get(API_ENDPOINTS.SYSTEM.LOGS, { params })
  },

  /**
   * 获取缓存状态
   */
  getCacheStatus: async () => {
    return apiClient.get(API_ENDPOINTS.SYSTEM.CACHE.STATUS)
  },

  /**
   * 清理缓存
   * @param cacheType 缓存类型
   */
  clearCache: async (cacheType?: string) => {
    return apiClient.post(API_ENDPOINTS.SYSTEM.CACHE.CLEAR, {
      cache_type: cacheType
    })
  },

  // ========== 词库管理 ==========
  
  /**
   * 获取词库列表
   * @param params 查询参数
   */
  getLexiconList: async (params?: any) => {
    return apiClient.get(API_ENDPOINTS.LEXICON.LIST, { params })
  },

  /**
   * 获取词库详情
   * @param lexiconId 词库ID
   */
  getLexiconDetail: async (lexiconId: number) => {
    return apiClient.get(API_ENDPOINTS.LEXICON.DETAIL(lexiconId))
  },

  /**
   * 导入词库
   * @param file 文件
   * @param options 导入选项
   */
  importLexicon: async (file: File, options?: any) => {
    const formData = new FormData()
    formData.append('file', file)
    if (options) {
      Object.keys(options).forEach(key => {
        formData.append(key, options[key])
      })
    }
    return apiClient.post(API_ENDPOINTS.LEXICON.IMPORT, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  /**
   * 删除词库
   * @param lexiconId 词库ID
   */
  deleteLexicon: async (lexiconId: number) => {
    return apiClient.delete(API_ENDPOINTS.LEXICON.DELETE(lexiconId))
  },

  /**
   * 批量删除词库
   * @param lexiconIds 词库ID数组
   */
  batchDeleteLexicon: async (lexiconIds: number[]) => {
    return apiClient.post(API_ENDPOINTS.LEXICON.BATCH_DELETE, {
      lexicon_ids: lexiconIds
    })
  },

  /**
   * 获取词库统计
   */
  getLexiconStats: async () => {
    return apiClient.get(API_ENDPOINTS.LEXICON.STATS)
  },

  // ========== AI服务 ==========
  
  /**
   * 获取AI服务商列表
   */
  getAIProviders: async () => {
    return apiClient.get(API_ENDPOINTS.AI.PROVIDERS)
  },

  /**
   * 获取AI服务商状态
   * @param provider 服务商名称
   */
  getAIProviderStatus: async (provider: string) => {
    return apiClient.get(API_ENDPOINTS.AI.PROVIDER_STATUS(provider))
  },

  /**
   * 获取Token统计
   * @param params 查询参数
   */
  getTokenStats: async (params?: any) => {
    return apiClient.get(API_ENDPOINTS.AI.TOKEN_STATS, { params })
  },

  /**
   * 获取使用历史
   * @param params 查询参数
   */
  getUsageHistory: async (params?: any) => {
    return apiClient.get(API_ENDPOINTS.AI.USAGE_HISTORY, { params })
  }
}

// 导出为默认
export default adminApi